import React from 'react';
import _ from 'underscore';

import ReactionSchemeThumbnail from 'chemistry/reactions/ReactionSchemeThumbnail';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import {ReactionGraphQL} from '../../graphql-fragments/chemistry/__generated/ReactionGraphQL';
import * as ReactionContextMenu from './ReactionContextMenu';

export interface ReactionListingExtras {}
export type ReactionColumnExtras = ReactionListingExtras;

const REACTION_SCHEME_HEIGHT = 160;

export default class ReactionListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.REACTION> {
  listingRendererType = ListingRendererType.REACTION;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon={FolderItemIcon.Reaction} className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    items: Array<ReactionGraphQL>,
    extras: ReactionListingExtras
  ): Array<ColumnConfiguration<ReactionGraphQL>> {
    return _.compact([
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: 'ID',
          flexGrow: 0,
          isSortable: false,
          width: 120,
        },
        render: (item) => item.id,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
          width: 200,
        },
        render: (item) => (
          <span key="name" className="sidepanel-primaryLabel">
            {item.name}
          </span>
        ),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.REACTION_SCHEME,
          header: 'Reaction scheme',
          flexGrow: 1,
          isSortable: false,
          width: 400,
          fillCell: true,
        },
        render: (item) =>
          item.scheme && (
            <ReactionSchemeThumbnail
              key={item.scheme}
              src={item.scheme}
              alt={`Reaction scheme for ${item.name}`}
            />
          ),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATOR,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATOR_NAME),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATOR_NAME,
        },
        render: (item) => getPartyDisplayString(item.creator),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.PARENT_ENTRY,
          header: 'Parent Entry',
          flexGrow: 1,
          isSortable: false,
        },
        render: (item) => (item.entry?.__typename === 'EntryNode' ? item.entry.name : null),
      },
    ]);
  }

  getNonPluginRowContentHeight(item: ReactionGraphQL, _extras: ReactionListingExtras): number {
    return item.scheme ? REACTION_SCHEME_HEIGHT : TABLE_ROW_BODY_HEIGHT;
  }

  openContextMenu(item: ReactionGraphQL, e: BasicEvent, _options: unknown): void {
    ReactionContextMenu.open(e.nativeEvent, item);
  }
}
